import classNames from "classnames";
import { ReactNode, useEffect, useState } from "react";
import { ModalComponent } from "..";
import { useModalControl } from "../../../customHooks/useModalControl";
import {
  copyToClipBoard,
  decodeUrl,
  formatToCurrency,
  formatToNumber,
  getChannnelText,
  getUploadStatus,
} from "../../../helperFunctions";
import { SingleContributionLog } from "../../../redux/features/contributions/contributions.type";
import { SingleDepositLog } from "../../../redux/features/deposits/deposits.type";
import { SingleCooperativeTransaction } from "../../../redux/features/finances/finances.type";
import { SingleSharesLog } from "../../../redux/features/shares/shares.type";
import {
  ManualPaymentMethodsMap,
  transactioncategoryMap,
} from "../../../utils/constants";
import { TransactionCategory } from "../../../utils/types";
import { Button } from "../../buttons/button";
import { TagCard, TagType } from "../../cards/tagCard";
import { Icon } from "../../icons/icon";
import { TimeText } from "../../typography/timeText";
import { ImageViewModal } from "./imageViewModal";

export const TransactionDetailsModal = ({
  transaction,
  isModalOpen,
  onModalClose,
  type,
}: {
  transaction:
    | SingleContributionLog
    | SingleDepositLog
    | SingleSharesLog
    | SingleCooperativeTransaction;
  isModalOpen: boolean;
  onModalClose: () => void;
  type: TransactionCategory;
}) => {
  const [data, setData] = useState<
    (
      | {
          title: string;
          value: ReactNode;
        }
      | undefined
    )[]
  >([]);
  const [proofUrl, setProofUrl] = useState<string>();

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  useEffect(() => {
    isModalOpen &&
      setData([
        {
          title: "Type",
          value: transactioncategoryMap[type].title,
        },
        {
          title: "Status",
          value: (
            <TagCard
              text={
                getUploadStatus(
                  transaction?.isApprove && transaction?.isPaymentMade,
                  transaction?.ApproveLevel ?? 0,
                  transaction.inAppPayment,
                  transaction.flow_type === "in" ? "credit" : "debit"
                ).text
              }
              type={
                getUploadStatus(
                  transaction?.isApprove && transaction?.isPaymentMade,
                  transaction?.ApproveLevel ?? 0,
                  transaction.inAppPayment,
                  transaction.flow_type === "in" ? "credit" : "debit"
                ).type as TagType
              }
            />
          ),
        },

        {
          title: "Amount",
          value: (
            <span
              className={classNames("font-bold", {
                "text-success-600": transaction?.flow_type === "in",
                "text-error-600": transaction?.flow_type === "out",
              })}
            >
              {transaction?.flow_type === "in" ? "+" : "-"}
              {formatToCurrency(transaction?.Amount, {
                decimalPlaces: 2,
                currency: "NGN",
              })}
            </span>
          ),
        },

        type === "shares" && (transaction as SingleSharesLog)?.Unit
          ? {
              title: "Share Quantity",
              value: (
                <span className="font-bold">
                  {formatToNumber((transaction as SingleSharesLog)?.Unit, {
                    decimalPlaces: 2,
                  })}{" "}
                  units
                </span>
              ),
            }
          : undefined,

        transaction?.reference
          ? {
              title: "Reference",
              value: (
                <button
                  type="button"
                  className="font-bold flex items-center gap-2 w-fit"
                  onClick={() => {
                    copyToClipBoard(transaction?.reference);
                  }}
                >
                  {transaction?.reference}
                  <Icon
                    svg={"copy-icon"}
                    width={16}
                    height={16}
                    className={"text-pry-green-300 cursor-pointer"}
                  />
                </button>
              ),
            }
          : undefined,

        transaction.flow_type === "in"
          ? {
              title: "Payment Channel",
              value: (
                <TagCard
                  text={getChannnelText(transaction?.inAppPayment)}
                  type={transaction?.inAppPayment ? "success" : "info"}
                />
              ),
            }
          : undefined,

        transaction?.paymentMode && transaction.flow_type === "in"
          ? {
              title: "Payment Method",
              value: (
                <TagCard
                  text={ManualPaymentMethodsMap[transaction.paymentMode]}
                  type={"info"}
                />
              ),
            }
          : undefined,

        {
          title: "Payment Date",
          value: (
            <TimeText timeString={transaction?.PaymentDate} showTime={true} />
          ),
        },

        !transaction.inAppPayment && transaction.flow_type === "in"
          ? {
              title: "Upload Date",
              value: (
                <TimeText
                  timeString={transaction?.PaymentDateAdded}
                  showTime={true}
                />
              ),
            }
          : undefined,

        transaction.payslip
          ? {
              title: "Payment Proof",
              value: (
                <Button
                  type="button"
                  text="View"
                  className="!px-0 !py-0 md:!px-0 md:!py-0 !w-fit"
                  variation="plain"
                  onClick={() => {
                    setProofUrl(transaction.payslip);
                    handleModalOpen("imageViewModal");
                  }}
                />
              ),
            }
          : undefined,
        {
          title: "Detail",
          value: transaction.description ?? "N/A",
        },
      ]);
    // eslint-disable-next-line
  }, [isModalOpen]);

  return (
    <>
      <ModalComponent
        isModalOpen={isModalOpen}
        onModalClose={() => {
          onModalClose();
        }}
        title={`Transaction Details`}
        topTitle
        childrenClassName=""
        modalClassName="!min-h-[490px]"
        hasButtons={false}
      >
        <div className="flex flex-col">
          {data?.map((itm, idx) => {
            const key = `transaction-details-${idx}`;
            return itm ? (
              <div
                className="
                py-3 border-b flex 
                items-center justify-between 
                first:!pt-0 last:!border-b-0
                text-xs gap-3
                "
                key={key}
              >
                <p className="font-medium">{itm?.title}:</p>
                <div className="text-right">{itm?.value}</div>
              </div>
            ) : undefined;
          })}
        </div>
      </ModalComponent>

      {proofUrl ? (
        <ImageViewModal
          modalOpen={modalOpen && modal === "imageViewModal"}
          onModalClose={() => {
            setProofUrl(undefined);
            handleModalClose();
          }}
          src={decodeUrl(proofUrl)}
        />
      ) : undefined}
    </>
  );
};
